import { useEffect } from "react";
import { BsCheckLg, BsExclamationTriangle, BsInfoCircle, BsXCircle, BsX } from "react-icons/bs";

// Configuration moderne des toasts avec le système de design établi
const toastConfig = {
  success: {
    style: "bg-green-50 text-green-800 border-green-200",
    accent: "border-l-green-500",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    icon: <BsCheckLg className="h-5 w-5" />,
  },
  error: {
    style: "bg-red-50 text-red-800 border-red-200",
    accent: "border-l-red-500",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    icon: <BsXCircle className="h-5 w-5" />,
  },
  warning: {
    style: "bg-yellow-50 text-yellow-800 border-yellow-200",
    accent: "border-l-yellow-500",
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-600",
    icon: <BsExclamationTriangle className="h-5 w-5" />,
  },
  info: {
    style: "bg-blue-50 text-blue-800 border-blue-200",
    accent: "border-l-blue-500",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    icon: <BsInfoCircle className="h-5 w-5" />,
  },
};

export type ToastType = "success" | "error" | "warning" | "info";

export interface U2000ToastProps {
  id: string;
  title?: string;
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: (id: string) => void;
}

export function U2000Toast({ id, title, message, type = "info", duration = 3000, onClose }: U2000ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => onClose(id), duration);
    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  // Configuration moderne avec système de design cohérent
  const config = toastConfig[type] || toastConfig.info;

  return (
    <div
      className={`relative flex w-full max-w-sm items-start gap-3 rounded-lg border border-l-4 p-4 shadow-lg 
                 ${config.style} ${config.accent} animate-slide-up transition-all duration-200`}
      role="alert"
      aria-live="assertive"
    >
      {/* Icône avec fond circulaire moderne */}
      <div className={`shrink-0 flex items-center justify-center w-8 h-8 rounded-full ${config.iconBg}`}>
        <div className={config.iconColor}>{config.icon}</div>
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        {title && <h3 className="text-sm font-semibold mb-1 leading-tight">{title}</h3>}
        <p className="text-sm leading-relaxed">{message}</p>
      </div>

      {/* Bouton de fermeture moderne */}
      <button
        className="shrink-0 flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 
                   rounded transition-colors duration-200 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
        onClick={() => onClose(id)}
        aria-label="Fermer la notification"
        title="Fermer"
      >
        <BsX className="h-4 w-4" />
      </button>
    </div>
  );
}
